import { useState, useEffect, useRef, useCallback } from 'react';
import { io } from 'socket.io-client';
import { formatTimeOfDay, getEarliestEffectiveDismissalTime, getEarliestTimeOfDay } from '@car-pool/shared/dismissalTimes';
import { resolveStudentCarpoolLine } from '@car-pool/shared/carpoolLines';
import DashboardHeader from '../components/DashboardHeader';
import { apiFetch, SOCKET_URL } from '../config/api';
import { getFallbackGradeLabel } from '../lib/gradeLabels';
import {
  buildCalledPickupDestinations,
  getDismissalStatusDestination,
  getPickupDestination,
} from '../lib/pickupDestinations';
import styles from './DismissalBoard.module.css';

const AVATAR_COLORS = ['#4a8fd4','#22c55e','#8b5cf6','#f97316','#14b8a6','#ec4899','#f59e0b','#6366f1'];

function initials(name = '') {
  const p = name.trim().split(' ');
  return ((p[0]?.[0] ?? '') + (p[1]?.[0] ?? '')).toUpperCase();
}

function Avatar({ name, size = 36, index = 0 }) {
  const color = AVATAR_COLORS[index % AVATAR_COLORS.length];
  return (
    <div
      style={{
        width: size, height: size, borderRadius: '50%',
        background: color, display: 'flex', alignItems: 'center',
        justifyContent: 'center', color: '#fff',
        fontWeight: 700, fontSize: size * 0.32, flexShrink: 0,
      }}
    >
      {initials(name)}
    </div>
  );
}

const formatTime = () => {
  const d = new Date();
  return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
};

function normalizeIntegerArray(value, options = {}) {
  const { positiveOnly = false } = options;
  if (value === undefined || value === null || value === '') return [];

  const values = Array.isArray(value) ? value : [value];
  const parsed = [];

  values
    .flatMap((item) => String(item).split(','))
    .map((item) => item.trim())
    .filter(Boolean)
    .forEach((item) => {
      if (!/^-?\d+$/.test(item)) return;
      const number = Number.parseInt(item, 10);
      if (!Number.isInteger(number)) return;
      if (positiveOnly && number <= 0) return;
      parsed.push(number);
    });

  return [...new Set(parsed)];
}

function normalizeSelectedGroups(groups = []) {
  return (groups || [])
    .map((group) => {
      if (typeof group === 'number' || typeof group === 'string') {
        const text = String(group);
        if (text.startsWith('split:')) {
          const splitId = Number(text.slice('split:'.length));
          return Number.isInteger(splitId) ? { type: 'split', grade_split_id: splitId } : null;
        }
        if (text.startsWith('grade:')) {
          const grade = Number(text.slice('grade:'.length));
          return Number.isInteger(grade) ? { type: 'grade', grade } : null;
        }
        const grade = Number(group);
        return Number.isInteger(grade) ? { type: 'grade', grade } : null;
      }

      if (group?.type === 'split') {
        const splitId = Number(group.grade_split_id ?? group.id);
        return Number.isInteger(splitId) ? { ...group, type: 'split', grade_split_id: splitId } : null;
      }

      if (group?.type === 'split_code') {
        const splitIds = normalizeIntegerArray(group.grade_split_ids ?? group.grade_split_id ?? group.id, { positiveOnly: true });
        const fallbackGrades = normalizeIntegerArray(group.fallback_grades ?? group.grades ?? group.grade);
        return splitIds.length || fallbackGrades.length
          ? { ...group, type: 'split_code', grade_split_ids: splitIds, fallback_grades: fallbackGrades }
          : null;
      }

      const grade = Number(group?.grade ?? group?.grade_code);
      return Number.isInteger(grade) ? { ...group, type: 'grade', grade } : null;
    })
    .filter(Boolean);
}

// Label shown next to the student name, matching the school's grouping setting:
// 'grades' shows the grade, 'splits_only' the split code, 'grades_and_splits' both.
function buildStudentGroupLabel({ student, groupingMode, gradeLabelByCode, splitById }) {
  const grade = Number(student?.grade);
  const hasGrade = Number.isInteger(grade);
  const gradeLabel = hasGrade
    ? gradeLabelByCode.get(grade) || getFallbackGradeLabel(grade)
    : '';
  const split = splitById.get(Number(student?.grade_split_id));
  const splitCode = String(split?.split_code || '').trim();

  if (groupingMode === 'splits_only') return splitCode || gradeLabel;
  if (groupingMode === 'grades_and_splits' && splitCode) {
    return gradeLabel ? `${gradeLabel} - ${splitCode}` : splitCode;
  }
  return gradeLabel;
}

function groupMatchesStudent(groups, student) {
  if (!groups.length) return true;

  const studentGrade = Number(student?.grade);
  const rawStudentSplitId = student?.grade_split_id;
  const studentSplitId = Number(rawStudentSplitId);
  const hasStudentSplit = rawStudentSplitId !== null
    && rawStudentSplitId !== undefined
    && rawStudentSplitId !== ''
    && Number.isInteger(studentSplitId);

  return groups.some((group) => {
    if (group.type === 'split') return hasStudentSplit && Number(group.grade_split_id) === studentSplitId;
    if (group.type === 'split_code') {
      if (hasStudentSplit && (group.grade_split_ids || []).map(Number).includes(studentSplitId)) return true;
      return !hasStudentSplit && (group.fallback_grades || []).map(Number).includes(studentGrade);
    }
    return Number(group.grade) === studentGrade;
  });
}

export default function DismissalBoard({ user, onLogout, selectedGrades = [], onBack }) {
  const permissionLevel = String(user?.permission_level || '').toUpperCase();
  const canManage = ['ADMIN', 'SUPER_ADMIN/SUPPORT'].includes(permissionLevel);
  const canSendToCarpool = canManage || permissionLevel === 'TEACHER';
  const [waiting, setWaiting] = useState([]);
  const [called, setCalled] = useState([]);
  const [sent, setSent] = useState([]);
  const [pickedUpCount, setPickedUpCount] = useState(0);
  const [totalStudents, setTotalStudents] = useState(0);
  const [loading, setLoading] = useState(true);
  const [markingWalkInId, setMarkingWalkInId] = useState(null);
  // student_id -> the adult or bus waiting for this called student.
  const [pickupDestinations, setPickupDestinations] = useState({});
  // student_id -> { expected_contact_name }, for arrivals that did not match the plan.
  const [unscheduledPickups, setUnscheduledPickups] = useState({});
  const [toasts, setToasts] = useState([]);
  const [expandedWaitingId, setExpandedWaitingId] = useState(null);
  const [expandedSentId, setExpandedSentId] = useState(null);
  const [mobileTab, setMobileTab] = useState('waiting');
  const [school, setSchool] = useState(null);
  const [dismissalTime, setDismissalTime] = useState(null);
  const toastIdRef = useRef(0);
  const boardStudentsRef = useRef(new Map());
  const dismissalStatusRef = useRef(new Map());
  const busByStudentRef = useRef(new Map());
  const socketConnectedRef = useRef(false);

  // Keep refs so socket callback can access latest state without re-subscribing
  const waitingRef = useRef(waiting);
  waitingRef.current = waiting;
  const selectedGradesRef = useRef(selectedGrades);
  selectedGradesRef.current = selectedGrades;

  const applyDismissalStatus = useCallback((payload = {}) => {
    const studentId = Number(payload.student_id);
    const status = String(payload.status || '').toLowerCase();
    const student = boardStudentsRef.current.get(studentId);
    if (!studentId || !student || !['waiting', 'called', 'sent', 'picked_up'].includes(status)) {
      return false;
    }

    const previousStatus = String(dismissalStatusRef.current.get(studentId)?.status || 'waiting').toLowerCase();
    dismissalStatusRef.current.set(studentId, {
      ...(dismissalStatusRef.current.get(studentId) || {}),
      ...payload,
      student_id: studentId,
      status,
    });

    const savedDestination = getDismissalStatusDestination(payload);
    if (status === 'called' && savedDestination) {
      setPickupDestinations((current) => ({
        ...current,
        [studentId]: savedDestination,
      }));
    }

    setWaiting((current) => {
      const withoutStudent = current.filter((item) => item.id !== studentId);
      return status === 'waiting' ? [...withoutStudent, student] : withoutStudent;
    });
    setCalled((current) => {
      const withoutStudent = current.filter((item) => item.id !== studentId);
      return status === 'called' ? [...withoutStudent, student] : withoutStudent;
    });
    setSent((current) => {
      const withoutStudent = current.filter((item) => item.id !== studentId);
      if (status !== 'sent') return withoutStudent;
      const sentAt = payload.sent_at
        ? new Date(payload.sent_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
        : formatTime();
      return [...withoutStudent, { ...student, sentAt }];
    });

    const wasPickedUp = previousStatus === 'picked_up';
    const isPickedUp = status === 'picked_up';
    if (wasPickedUp !== isPickedUp) {
      setPickedUpCount((current) => Math.max(0, current + (isPickedUp ? 1 : -1)));
    }
    if (status === 'waiting' || status === 'picked_up') {
      setPickupDestinations((current) => {
        if (!current[studentId]) return current;
        const next = { ...current };
        delete next[studentId];
        return next;
      });
    }
    setExpandedWaitingId((current) => (current === studentId ? null : current));
    setExpandedSentId((current) => (current === studentId ? null : current));
    return true;
  }, []);

  const syncDismissalStatuses = useCallback(async () => {
    if (!user.school_id || !user.token || !boardStudentsRef.current.size) return;

    try {
      const response = await apiFetch(`/dismissal-status?school_id=${user.school_id}`, {}, user.token);
      const statuses = response.data || [];
      const statusByStudentId = new Map(
        statuses.map((status) => [Number(status.student_id), status])
      );
      dismissalStatusRef.current = statusByStudentId;

      const nextWaiting = [];
      const nextCalled = [];
      const nextSent = [];
      let nextPickedUpCount = 0;
      boardStudentsRef.current.forEach((student, studentId) => {
        const dismissalStatus = statusByStudentId.get(studentId);
        const status = String(dismissalStatus?.status || 'waiting').toLowerCase();
        if (status === 'called') {
          nextCalled.push(student);
        } else if (status === 'sent') {
          const sentAt = dismissalStatus?.sent_at
            ? new Date(dismissalStatus.sent_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
            : formatTime();
          nextSent.push({ ...student, sentAt });
        } else if (status === 'picked_up') {
          nextPickedUpCount += 1;
        } else {
          nextWaiting.push(student);
        }
      });

      setWaiting(nextWaiting);
      setCalled(nextCalled);
      setSent(nextSent);
      setPickedUpCount(nextPickedUpCount);
      setPickupDestinations((current) => {
        const rebuilt = buildCalledPickupDestinations({ dismissalStatuses: statuses });
        nextCalled.forEach((student) => {
          if (!rebuilt[student.id] && current[student.id]) {
            rebuilt[student.id] = current[student.id];
          }
        });
        return rebuilt;
      });
    } catch (error) {
      console.error('Failed to synchronize dismissal statuses:', error);
    }
  }, [user.school_id, user.token]);

  const addToast = useCallback((message) => {
    const id = ++toastIdRef.current;
    setToasts((prev) => [...prev, { id, message }]);
    setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 4000);
  }, []);

  const updateStatus = useCallback((studentId, status) => {
    if (!canManage && !(canSendToCarpool && status === 'sent')) return Promise.resolve(null);
    return apiFetch('/dismissal-status', {
      method: 'PUT',
      body: JSON.stringify({ student_id: studentId, school_id: user.school_id, status }),
    }, user.token).catch((err) => {
      console.error('Failed to update dismissal status:', err);
      return null;
    });
  }, [canManage, canSendToCarpool, user.school_id, user.token]);

  const callStudentLocal = useCallback((student) => {
    setWaiting((prev) => prev.filter((s) => s.id !== student.id));
    setCalled((prev) => {
      if (prev.some((s) => s.id === student.id)) return prev;
      return [...prev, student];
    });
    setExpandedWaitingId((prev) => (prev === student.id ? null : prev));
  }, []);

  const callStudent = useCallback((student) => {
    if (!canManage) return;
    callStudentLocal(student);
    updateStatus(student.id, 'called');
    setExpandedWaitingId(null);
  }, [callStudentLocal, canManage, updateStatus]);

  const toggleExpandWaiting = useCallback((studentId) => {
    setExpandedWaitingId((prev) => (prev === studentId ? null : studentId));
  }, []);

  const toggleExpandSent = useCallback((studentId) => {
    setExpandedSentId((prev) => (prev === studentId ? null : studentId));
  }, []);

  const moveBackToCalled = useCallback((student) => {
    if (!canManage) return;
    setSent((prev) => prev.filter((s) => s.id !== student.id));
    setCalled((prev) => {
      if (prev.some((s) => s.id === student.id)) return prev;
      const { sentAt, ...rest } = student;
      return [...prev, rest];
    });
    setExpandedSentId(null);
    updateStatus(student.id, 'called');
  }, [canManage, updateStatus]);

  const loadBoard = useCallback(async () => {
    try {
      const groups = normalizeSelectedGroups(selectedGradesRef.current);
      const params = new URLSearchParams({ status: 'active' });
      if (user.school_id) params.append('school_id', user.school_id);
      const gradeParams = new Set();
      const splitParams = new Set();
      const fallbackGradeParams = new Set();

      groups.forEach((group) => {
        if (group.type === 'split') {
          splitParams.add(Number(group.grade_split_id));
        } else if (group.type === 'split_code') {
          (group.grade_split_ids || []).forEach((splitId) => splitParams.add(Number(splitId)));
          (group.fallback_grades || []).forEach((grade) => fallbackGradeParams.add(Number(grade)));
        } else {
          gradeParams.add(Number(group.grade));
        }
      });
      [...gradeParams]
        .filter((grade) => Number.isInteger(grade))
        .forEach((grade) => params.append('grade', grade));
      [...splitParams]
        .filter((splitId) => Number.isInteger(splitId) && splitId > 0)
        .forEach((splitId) => params.append('grade_split_id', splitId));
      [...fallbackGradeParams]
        .filter((grade) => Number.isInteger(grade))
        .forEach((grade) => params.append('grade_without_split', grade));

      // Fetch students, today's status, and line timing metadata in parallel.
      const [studentsRes, statusRes, schoolRes, campusesRes, linesRes, assignmentsRes, gradeNamesRes, gradeSplitsRes] = await Promise.all([
        apiFetch(`/students?${params.toString()}`, {}, user.token),
        apiFetch(`/dismissal-status?school_id=${user.school_id}`, {}, user.token),
        apiFetch(`/schools/${user.school_id}`, {}, user.token).catch(() => ({ data: null })),
        apiFetch(`/campuses/school/${user.school_id}`, {}, user.token).catch(() => ({ data: [] })),
        apiFetch('/carpool-lines', {}, user.token).catch(() => ({ data: [] })),
        apiFetch('/student-line-assigned/active', {}, user.token).catch(() => ({ data: [] })),
        apiFetch(`/school-grade-names?school_id=${user.school_id}&is_active=1`, {}, user.token).catch(() => ({ data: [] })),
        apiFetch(`/school-grade-splits?school_id=${user.school_id}&is_active=true`, {}, user.token).catch(() => ({ data: [] })),
      ]);

      const groupingMode = schoolRes.data?.student_grouping_mode || 'grades';
      const gradeLabelByCode = new Map(
        (gradeNamesRes.data || [])
          .filter((g) => g.label && Number.isInteger(Number(g.grade_code)))
          .map((g) => [Number(g.grade_code), g.label])
      );
      const splitById = new Map(
        (gradeSplitsRes.data || []).map((split) => [Number(split.id), split])
      );

      const rawStudents = studentsRes.data || [];
      const students = rawStudents.map((student) => ({
        id: student.id,
        name: `${student.f_name} ${student.l_name}`,
        grade: student.grade,
        grade_split_id: student.grade_split_id,
        groupLabel: buildStudentGroupLabel({ student, groupingMode, gradeLabelByCode, splitById }),
      }));
      boardStudentsRef.current = new Map(students.map((student) => [Number(student.id), student]));
      const studentIds = rawStudents.map((student) => Number(student.id)).filter(Boolean);
      const dismissalDate = String(
        (statusRes.data || []).find((status) => status.dismissal_date)?.dismissal_date
        || new Date().toISOString()
      ).slice(0, 10);
      const needsDestinationFallback = (statusRes.data || []).some((status) => (
        status.status === 'called' && !getDismissalStatusDestination(status)
      ));
      const [effectiveTimesRes, destinationFallback] = await Promise.all([
        studentIds.length
          ? apiFetch(`/dismissal-time-defaults/effective?student_ids=${studentIds.join(',')}`, {}, user.token).catch(() => ({ data: [] }))
          : Promise.resolve({ data: [] }),
        needsDestinationFallback
          ? (async () => {
              const [geofenceResponse, busesResponse] = await Promise.all([
                apiFetch(`/geofenced-data/school/${user.school_id}/current`, {}, user.token).catch(() => ({ data: [] })),
                apiFetch(`/buses?school_id=${user.school_id}`, {}, user.token).catch(() => ({ data: [] })),
              ]);
              const activeBuses = (busesResponse.data || []).filter((bus) => bus.is_active);
              const rosterResponses = await Promise.all(activeBuses.map((bus) => (
                apiFetch(`/buses/${bus.id}/roster?date=${encodeURIComponent(dismissalDate)}`, {}, user.token)
                  .catch(() => ({ data: [] }))
              )));
              const busByStudentId = new Map();
              activeBuses.forEach((bus, index) => {
                (rosterResponses[index]?.data || []).forEach((student) => {
                  busByStudentId.set(Number(student.id), bus);
                });
              });
              return { geofenceEvents: geofenceResponse.data || [], busByStudentId };
            })()
          : Promise.resolve({ geofenceEvents: [], busByStudentId: new Map() }),
      ]);
      const { geofenceEvents, busByStudentId } = destinationFallback;
      busByStudentRef.current = busByStudentId;
      const effectiveTimeByStudentId = new Map(
        (effectiveTimesRes.data || []).map((item) => [Number(item.student_id), item])
      );
      const boardStudentIds = new Set(rawStudents.map((student) => Number(student.id)));
      const campusIds = new Set((campusesRes.data || []).map((campus) => Number(campus.campus_id)));
      const schoolLines = (linesRes.data || []).filter((line) => campusIds.has(Number(line.campus_id)));
      const assignmentByStudentId = new Map(
        (assignmentsRes.data || [])
          .filter((assignment) => boardStudentIds.has(Number(assignment.student_id)))
          .map((assignment) => [Number(assignment.student_id), assignment])
      );
      const defaultDismissalTime = schoolRes.data?.default_dismissal_time || null;
      const studentDismissalTimes = rawStudents.map((student) => {
        const effectiveTime = effectiveTimeByStudentId.get(Number(student.id));
        if (effectiveTime) {
          return effectiveTime.no_school || effectiveTime.source === 'calendar_no_school'
            ? null
            : effectiveTime.dismissal_time;
        }

        const assignment = assignmentByStudentId.get(Number(student.id));
        const { line } = resolveStudentCarpoolLine({
          student,
          explicitAssignment: assignment,
          lines: schoolLines,
          groupingMode,
        });
        return line?.dismissal_time || defaultDismissalTime;
      });
      const allLoadedStudentsHaveNoSchool = rawStudents.length > 0
        && rawStudents.every((student) => {
          const effectiveTime = effectiveTimeByStudentId.get(Number(student.id));
          return effectiveTime?.no_school || effectiveTime?.source === 'calendar_no_school';
        });
      const nextDismissalTime = allLoadedStudentsHaveNoSchool
        ? null
        : getEarliestTimeOfDay(studentDismissalTimes)
          || getEarliestEffectiveDismissalTime({ school: schoolRes.data, lines: schoolLines });

      setSchool(schoolRes.data || null);
      setDismissalTime(nextDismissalTime);

      // Build status map from today's dismissal records
      const statusMap = {};
      (statusRes.data || []).forEach((d) => { statusMap[d.student_id] = d; });
      dismissalStatusRef.current = new Map(
        (statusRes.data || []).map((status) => [Number(status.student_id), status])
      );

      const w = [], c = [], s = [];
      let pickedUp = 0;
      students.forEach((st) => {
        const ds = statusMap[st.id];
        if (!ds || ds.status === 'waiting') {
          w.push(st);
        } else if (ds.status === 'called') {
          c.push(st);
        } else if (ds.status === 'sent') {
          const sentAt = ds.sent_at
            ? new Date(ds.sent_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
            : formatTime();
          s.push({ ...st, sentAt });
        } else if (ds.status === 'picked_up') {
          pickedUp += 1;
        }
      });

      setWaiting(w);
      setCalled(c);
      setSent(s);
      setPickedUpCount(pickedUp);
      setTotalStudents(students.length);
      setPickupDestinations(buildCalledPickupDestinations({
        dismissalStatuses: statusRes.data || [],
        geofenceEvents,
        busByStudentId,
      }));
    } catch (err) {
      console.error('Failed to fetch students:', err);
    } finally {
      setLoading(false);
    }
  }, [user.token, user.school_id]);

  // Initial load + reload when grades change
  useEffect(() => {
    loadBoard();
  }, [loadBoard, selectedGrades]);

  // WebSocket: stable connection — does NOT depend on loadBoard/callStudent
  useEffect(() => {
    const socket = io(SOCKET_URL);
    socket.on('connect', () => {
      console.log('[WS] connected, joining school:', user.school_id);
      socket.emit('watch-school', user.school_id);
      if (socketConnectedRef.current) syncDismissalStatuses();
      socketConnectedRef.current = true;
    });

    socket.on('geofence-event', (payload) => {
      if (payload.action !== 'enter') return;

      // The status broadcast is sent just before this geofence event. Attach
      // the exact arrival that caused each call independently of React's state
      // update timing, so the card can tell the child where to go.
      const calledStudentIds = normalizeIntegerArray(payload.called_student_ids, { positiveOnly: true });
      if (calledStudentIds.length > 0) {
        setPickupDestinations((current) => {
          const next = { ...current };
          calledStudentIds.forEach((studentId) => {
            const destination = getPickupDestination(payload, studentId, busByStudentRef.current);
            if (destination) next[studentId] = destination;
          });
          return next;
        });
      }

      // The arriving adult is not the one this student's plan expected. Staff
      // are warned, not blocked — they confirm before sending the child out.
      const mismatches = (payload.pickup_plan || []).filter((entry) => entry.status === 'unscheduled');
      if (mismatches.length > 0) {
        setUnscheduledPickups((prev) => {
          const next = { ...prev };
          mismatches.forEach((entry) => {
            next[entry.student_id] = {
              expected_contact_name: entry.expected_contact_name,
              arrived_name: payload.parent_name || 'Someone else',
            };
          });
          return next;
        });
      }

      const childrenIds = payload.children_ids || [];
      const parentName = payload.parent_name || 'A parent';
      const groups = normalizeSelectedGroups(selectedGradesRef.current);

      // Find which of this parent's children are in the waiting list (and in monitored grades)
      const currentWaiting = waitingRef.current;
      const matchedStudents = currentWaiting.filter((s) =>
        childrenIds.includes(s.id) && groupMatchesStudent(groups, s)
      );

      if (canManage && matchedStudents.length > 0) {
        const names = matchedStudents.map((s) => s.name).join(', ');
        addToast(`${parentName} has arrived — calling ${names} to carpool`);

        // Auto-call matched students (local move + API update)
        matchedStudents.forEach((s) => {
          callStudentLocal(s);
          apiFetch('/dismissal-status', {
            method: 'PUT',
            body: JSON.stringify({ student_id: s.id, school_id: user.school_id, status: 'called' }),
          }, user.token).catch(() => {});
        });
      }
    });

    socket.on('dismissal-status-changed', (payload = {}) => {
      if (payload.school_id && Number(payload.school_id) !== Number(user.school_id)) return;
      if (!applyDismissalStatus(payload) && (!payload.student_id || !payload.status)) {
        syncDismissalStatuses();
      }
    });

    return () => {
      socketConnectedRef.current = false;
      socket.disconnect();
    };
  }, [user.school_id, user.token, addToast, applyDismissalStatus, callStudentLocal, canManage, syncDismissalStatuses]);

  const clearUnscheduled = (studentId) => {
    setUnscheduledPickups((prev) => {
      if (!prev[studentId]) return prev;
      const next = { ...prev };
      delete next[studentId];
      return next;
    });
  };

  const markSent = (student) => {
    if (!canSendToCarpool) return;

    const unscheduled = unscheduledPickups[student.id];
    if (unscheduled) {
      const expected = unscheduled.expected_contact_name || 'someone else';
      const arrived = unscheduled.arrived_name || 'the person waiting';
      if (!window.confirm(
        `${student.name} is scheduled to be collected by ${expected} today, but ${arrived} has arrived.\n\n`
        + 'Send them out anyway?'
      )) {
        return;
      }
    }
    clearUnscheduled(student.id);
    setCalled((prev) => prev.filter((s) => s.id !== student.id));
    setSent((prev) => [...prev, { ...student, sentAt: formatTime() }]);
    updateStatus(student.id, 'sent').then((response) => {
      if (response) return;
      addToast(`Could not send ${student.name} to carpool — refreshing the board`);
      syncDismissalStatuses();
    });
  };

  const markPickedUp = async (student, options = {}) => {
    if (!canManage || !student?.id || markingWalkInId) return;

    const isWalkIn = Boolean(options.walkIn);
    const confirmMessage = isWalkIn
      ? `Mark ${student.name} as picked up by walk-in? Use this only when the pickup contact is physically with the student.`
      : `Mark ${student.name} as picked up?`;
    if (typeof window !== 'undefined' && !window.confirm(confirmMessage)) {
      return;
    }

    setMarkingWalkInId(student.id);
    try {
      const response = await apiFetch('/dismissal-status', {
        method: 'PUT',
        body: JSON.stringify({ student_id: student.id, school_id: user.school_id, status: 'picked_up' }),
      }, user.token);

      applyDismissalStatus(response.data || {
        student_id: student.id,
        school_id: user.school_id,
        status: 'picked_up',
      });
      addToast(`${student.name} marked picked up${isWalkIn ? ' by walk-in' : ''}`);
    } catch (err) {
      console.error('Failed to mark walk-in pickup:', err);
      addToast(`Could not mark ${student.name} picked up`);
    } finally {
      setMarkingWalkInId(null);
    }
  };

  const dismissed = sent.length + pickedUpCount;

  return (
    <div className={styles.page}>
      <DashboardHeader
        user={user}
        school={school?.school_nickname || school?.legal_name || user?.school_name}
        dismissalTime={formatTimeOfDay(dismissalTime) || 'Not set'}
        onLogout={onLogout}
      />

      <div className={styles.body}>
        {onBack && (
          <button
            onClick={onBack}
            style={{
              background: 'none', border: 'none', color: 'rgba(255,255,255,0.7)',
              cursor: 'pointer', fontSize: '0.9rem', marginBottom: 8, padding: 0,
            }}
          >
            ← Back to Group Select
          </button>
        )}

        {!canManage && (
          <div className={styles.viewerBanner}>
            Carpool Viewer — tap a student in Called to send them to carpool. Other dismissal controls require a Carpool Manager.
          </div>
        )}

        <div className={`glass-card ${styles.board}`}>
          {loading ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'rgba(255,255,255,0.6)' }}>
              Loading students…
            </div>
          ) : (
            <>
              {/* Column 1: Waiting */}
              <div className={`${styles.col} ${mobileTab === 'waiting' ? styles.colActive : ''}`}>
                <div className={styles.colHeader}>
                  <div className={styles.colTitle}>Waiting for Pickup ({waiting.length})</div>
                  <div className={styles.colSub}>Students still in classroom</div>
                </div>
                <div className={styles.waitList}>
                  {waiting.map((s, i) => {
                    const isExpanded = expandedWaitingId === s.id;
                    return (
                      <div
                        key={s.id}
                        className={`${styles.waitItem} ${isExpanded ? styles.waitItemExpanded : ''}`}
                        data-track-subject-type="student"
                        data-track-subject-id={s.id}
                      >
                        <button
                          className={styles.waitRow}
                          data-track={isExpanded ? 'Collapse waiting student' : 'Expand waiting student'}
                          onClick={canManage ? () => toggleExpandWaiting(s.id) : undefined}
                          disabled={!canManage}
                        >
                          <Avatar name={s.name} size={38} index={i} />
                          <div className={styles.waitInfo}>
                            <span className={styles.waitName}>
                              {s.name}
                              {s.groupLabel && <span className={styles.groupTag}>{s.groupLabel}</span>}
                            </span>
                            <span className={styles.waitSub}>Awaiting pickup</span>
                          </div>
                          {canManage && <span className={styles.dots}>⋮</span>}
                        </button>
                        {canManage && isExpanded && (
                          <div className={styles.expandedActions}>
                            <button
                              className={styles.readyButton}
                              onClick={() => callStudent(s)}
                            >
                              Mark as Ready for Pickup?
                            </button>
                            <button
                              className={styles.walkInButton}
                              disabled={markingWalkInId === s.id}
                              onClick={() => markPickedUp(s, { walkIn: true })}
                            >
                              {markingWalkInId === s.id ? 'Marking...' : 'Walk-in Pickup'}
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Column 2: Called */}
              <div className={`${styles.col} ${mobileTab === 'called' ? styles.colActive : ''}`}>
                <div className={styles.colHeader}>
                  <div className={styles.colTitle}>Called To Carpool ({called.length})</div>
                  <div className={styles.colSub}>
                    {called.length === 0 ? 'Waiting for parent arrivals' : (canSendToCarpool ? 'Tap student to mark sent' : 'Monitoring live activity')}
                  </div>
                </div>
                {called.length === 0 ? (
                  <div className={styles.emptyHint}>Waiting for parent arrivals</div>
                ) : (
                  <div className={styles.calledGrid}>
                    {called.map((s, i) => (
                      <button
                        key={s.id}
                        className={styles.calledCard}
                        data-track="Send to carpool"
                        data-track-subject-type="student"
                        data-track-subject-id={s.id}
                        onClick={canSendToCarpool ? () => markSent(s) : undefined}
                        disabled={!canSendToCarpool}
                      >
                        <Avatar name={s.name} size={56} index={i} />
                        <div className={styles.calledInfo}>
                          <span className={styles.calledName}>{s.name}</span>
                          {pickupDestinations[s.id] ? (
                            <span className={styles.calledDestination}>
                              {pickupDestinations[s.id].label}
                            </span>
                          ) : null}
                          {/* Someone other than the scheduled adult has arrived
                              for this student — surfaced before staff tap, not
                              only in a dialog afterwards. */}
                          {unscheduledPickups[s.id] ? (
                            <span
                              className={styles.unscheduledFlag}
                              title={`Scheduled: ${unscheduledPickups[s.id].expected_contact_name || 'someone else'}`}
                            >
                              ⚠ Unscheduled pickup
                            </span>
                          ) : null}
                          {s.groupLabel && (
                            <span className={styles.groupTagDark}>{s.groupLabel}</span>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Column 3: Sent */}
              <div className={`${styles.col} ${mobileTab === 'sent' ? styles.colActive : ''}`}>
                <div className={styles.colHeader}>
                  <div className={styles.colTitle}>Sent To Carpool ({sent.length})</div>
                  <div className={styles.colSub}>
                    {sent.length === 0 ? 'Sent students will appear here' : (canManage ? 'Tap student to update' : 'Monitoring live activity')}
                  </div>
                </div>
                <div className={styles.sentList}>
                  {sent.map((s, i) => {
                    const isExpanded = expandedSentId === s.id;
                    return (
                      <div
                        key={s.id}
                        className={`${styles.sentItem} ${isExpanded ? styles.sentItemExpanded : ''}`}
                        data-track-subject-type="student"
                        data-track-subject-id={s.id}
                      >
                        <button
                          className={styles.sentRow}
                          data-track={isExpanded ? 'Collapse sent student' : 'Expand sent student'}
                          onClick={canManage ? () => toggleExpandSent(s.id) : undefined}
                          disabled={!canManage}
                        >
                          <Avatar name={s.name} size={36} index={i} />
                          <div className={styles.sentInfo}>
                            <span className={styles.sentName}>
                              {s.name}
                              {s.groupLabel && <span className={styles.groupTag}>{s.groupLabel}</span>}
                            </span>
                            <span className={styles.sentTime}>Sent at {s.sentAt}</span>
                          </div>
                          {canManage && <span className={styles.dots}>⋮</span>}
                        </button>
                        {canManage && isExpanded && (
                          <div className={styles.expandedActions}>
                            <button
                              className={styles.unsendButton}
                              onClick={() => moveBackToCalled(s)}
                            >
                              Move Back to Called?
                            </button>
                            <button
                              className={styles.walkInButton}
                              disabled={markingWalkInId === s.id}
                              onClick={() => markPickedUp(s)}
                            >
                              {markingWalkInId === s.id ? 'Marking...' : 'Mark Picked Up'}
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          <div className={styles.liveIndicator}>
            <span className={styles.liveDot} />
            <span className={styles.liveText}>LIVE</span>
          </div>
          <div className={styles.dismissed}>
            Dismissed: {dismissed} / {totalStudents} Students
          </div>
        </div>
      </div>

      {/* Mobile bottom navigation — switches between the three columns on phones */}
      {!loading && (
        <nav className={styles.mobileNav}>
          {[
            { key: 'waiting', label: 'Waiting', icon: '🕑', count: waiting.length },
            { key: 'called', label: 'Called', icon: '📣', count: called.length },
            { key: 'sent', label: 'Sent', icon: '✅', count: sent.length },
          ].map((tab) => (
            <button
              key={tab.key}
              className={`${styles.navItem} ${mobileTab === tab.key ? styles.navItemActive : ''}`}
              onClick={() => setMobileTab(tab.key)}
            >
              {mobileTab === tab.key && <span className={styles.navIndicator} />}
              <span className={styles.navIcon}>{tab.icon}</span>
              <span className={styles.navLabel}>{tab.label} ({tab.count})</span>
            </button>
          ))}
        </nav>
      )}

      {/* Toast notifications */}
      <div className={styles.toastContainer}>
        {toasts.map((t) => (
          <div key={t.id} className={styles.toast}>
            <span className={styles.toastIcon}>🚗</span>
            <span>{t.message}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
